import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Locale, DEFAULT_LOCALE } from "./types";

interface LocaleContextType {
  locale: Locale;
  /** Prefix a path with the locale (e.g. /en/about). French paths stay unprefixed. */
  localePath: (path: string) => string;
  /** Get the alternate locale path for language switcher */
  alternatePath: () => string;
}

const LocaleContext = createContext<LocaleContextType>({
  locale: DEFAULT_LOCALE,
  localePath: (p) => p,
  alternatePath: () => "/en",
});

export const useLocale = () => useContext(LocaleContext);

export const LocaleProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  const locale: Locale = location.pathname.startsWith("/en") ? "en" : "fr";

  const value = useMemo(() => {
    const localePath = (path: string) => {
      if (locale === "fr") return path;
      return `/en${path === "/" ? "" : path}`;
    };

    const alternatePath = () => {
      if (locale === "fr") {
        // Current FR path → EN equivalent
        return `/en${location.pathname === "/" ? "" : location.pathname}`;
      }
      // Current EN path → FR equivalent (remove /en prefix)
      const frPath = location.pathname.replace(/^\/en/, "") || "/";
      return frPath;
    };

    return { locale, localePath, alternatePath };
  }, [locale, location.pathname]);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};
